/**
 * Upload Limits Configuration
 *
 * Defines the maximum number of files and total storage size limits
 * for different file categories across the application.
 */

// File size in bytes
export const FILE_SIZE_LIMITS = {
  // Individual file size limits
  MAX_FILE_SIZE: 300 * 1024 * 1024, // 300MB per file (matches backend)
  MAX_GENERATION_SOURCE_FILE_SIZE: 5 * 1024 * 1024, // 5MB for generation source files (CVs, Forms)

  // Total storage limits per user
  MAX_TOTAL_STORAGE: 50 * 1024 * 1024 * 1024, // 50GB total storage per user

  // Warning threshold (show warning when user reaches this percentage)
  WARNING_THRESHOLD: 0.8, // 80%
} as const;

// File count limits
export const FILE_COUNT_LIMITS = {
  // Library files (CVs, Case Studies, Past Proposals)
  MAX_LIBRARY_FILES_TOTAL: 5000, // Total across all library categories
  MAX_CVS: 30,
  MAX_CASE_STUDIES: 40,
  MAX_COMPANY_INFO: 40,
  MAX_PAST_PROPOSALS: 30,

  // Tender-specific files
  MAX_SUPPORT_DOCS_PER_TENDER: 50, // Per tender support documents
  MAX_DRAFTS_PER_TENDER: 10, // Per tender draft documents
  MAX_TENDER_CVS: 40, // Per tender CVs
  MAX_TENDER_FORMS: 40, // Per tender forms/ad-hoc docs

  // Warning threshold
  WARNING_THRESHOLD: 0.8, // 80%
} as const;

/**
 * Format bytes to human-readable string
 */
export const formatBytes = (bytes: number | undefined | null, decimals = 2): string => {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
    return '-';
  }

  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / k ** i;

  // Use toFixed for precision, then parseFloat to remove trailing zeros
  const formatted = parseFloat(value.toFixed(dm));

  return `${formatted} ${sizes[i]}`;
};

/**
 * Calculate percentage of limit used
 */
export const calculateUsagePercentage = (current: number, max: number): number => {
  if (max === 0) {
    return 0;
  }
  return Math.min(Math.round((current / max) * 100), 100);
};

/**
 * Check if usage is approaching limit (at or above warning threshold)
 */
export const isApproachingLimit = (current: number, max: number): boolean => {
  return current / max >= FILE_SIZE_LIMITS.WARNING_THRESHOLD;
};

/**
 * Check if limit is exceeded
 */
export const isLimitExceeded = (current: number, max: number): boolean => {
  return current >= max;
};
